/* import __COLOCATED_TEMPLATE__ from './phone-handover-form.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type {
  FinVoiceNumberConfigurationHandoverConfiguration,
  HandoverConfiguration,
  HandoverConfigurationTools,
  ScheduledConfigurationKey,
} from 'embercom/models/fin-voice/number-configuration';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { handoverConfigErrorForConfigType } from 'embercom/lib/operator/fin-voice/number-configuration-helpers';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import { PhoneNumberType } from 'embercom/models/settings/calling';

interface Args {
  ruleset: $TSFixMe;
  phoneNumberId?: string;
  numberConfiguration?: FinVoiceNumberConfiguration;
  updateHandover: (handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration) => void;
  closeSection: () => void;
}

const DEFAULT_DEFLECTION_TIMER = 300; // 5 minutes
export const INTERCOM_IP_ALLOWLIST = ['192.167.213.124', '142.165.166.189', '192.127.200.109'];

export default class PhoneHandoverForm extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked configBeingEdited: ScheduledConfigurationKey = 'in';
  @tracked clonedHandoverConfig: FinVoiceNumberConfigurationHandoverConfiguration = {};

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.setInitialHandoverConfig();
  }

  get savingDisabled() {
    return (
      !!this.handoverConfigError ||
      this.args.ruleset.isSaving ||
      !this.args.phoneNumberId ||
      this.args.numberConfiguration?.isSaving
    );
  }

  get ruleset() {
    return this.args.ruleset;
  }

  get isExternalProvider() {
    return this.phoneNumber?.isExternalProvider || false;
  }

  get isSipNumber() {
    return this.phoneNumber?.phoneType === PhoneNumberType.Sip;
  }

  get phoneNumber() {
    if (!this.args.phoneNumberId) {
      return undefined;
    }

    return this.finVoiceConfigService.phoneNumbers[this.args.phoneNumberId];
  }

  get intercomIpAllowlist() {
    return INTERCOM_IP_ALLOWLIST;
  }

  get handoverOptions() {
    let options = [];

    let handoverToForwardCall = {
      icon: 'phone-transfer',
      value: 'handover-to-forward-call',
      text: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-forward-call',
      ),
    };

    let deflectEscalation = {
      icon: 'phone',
      value: 'deflect-escalation',
      text: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-deflect-escalation',
      ),
    };

    let handoverToVoicemail = {
      icon: 'phone-missed',
      value: 'handover-to-voicemail',
      text: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-voicemail',
      ),
    };

    let handoverToHuman = {
      icon: 'phone-hold',
      value: 'handover-to-human',
      text: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-human',
      ),
    };

    let handoverToWorkflow = {
      icon: 'phone',
      value: 'handover-to-workflow',
      text: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.action-handover-to-workflow',
      ),
    };

    if (this.isStandaloneApp) {
      options.push(deflectEscalation, handoverToForwardCall);
    } else if (this.isExternalProvider) {
      options.push(handoverToVoicemail, deflectEscalation, handoverToForwardCall);
    } else {
      options.push(
        handoverToVoicemail,
        handoverToHuman,
        deflectEscalation,
        handoverToWorkflow,
        handoverToForwardCall,
      );
    }

    return options;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get relevantHandoverConfig() {
    let officeHoursComparison = this.configBeingEdited;

    if (officeHoursComparison) {
      let handoverConfig = this.clonedHandoverConfig[officeHoursComparison];

      if (handoverConfig) {
        return handoverConfig;
      }
    }

    return this.clonedHandoverConfig['default'];
  }

  get predicatesSchedule() {
    return this.numberConfiguration?.predicates?.officeHoursSchedule;
  }

  get relevantDeflectEscalationConfig() {
    return this.relevantHandoverConfig?.type === 'deflect-escalation'
      ? this.relevantHandoverConfig
      : undefined;
  }

  get relevantHandoverToWorkflowConfig() {
    return this.relevantHandoverConfig?.type === 'handover-to-workflow'
      ? this.relevantHandoverConfig
      : undefined;
  }

  get handoverType() {
    return this.relevantHandoverConfig?.type;
  }

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get assignableAdmins() {
    return this.appService.app.assignableAdmins;
  }

  get assigneeId() {
    if (
      this.relevantHandoverConfig?.type === 'handover-to-human' ||
      this.relevantHandoverConfig?.type === 'handover-to-voicemail' ||
      this.relevantHandoverConfig?.type === 'handover-to-forward-call'
    ) {
      return this.relevantHandoverConfig?.config?.assigneeId;
    }

    return undefined;
  }

  get deflectionTimer() {
    return this.relevantHandoverConfig?.type === 'handover-to-human'
      ? (this.relevantHandoverConfig?.config?.deflectionTimer || DEFAULT_DEFLECTION_TIMER) / 60
      : undefined;
  }

  set deflectionTimer(value: number | undefined) {
    if (value !== undefined) {
      this.updateConfigField('handover-to-human', 'deflectionTimer', value * 60);
    }
  }

  private updateConfigField(
    expectedType: HandoverConfigurationTools,
    field: string,
    value: any,
  ): void {
    if (value === undefined || !this.relevantHandoverConfig) {
      return;
    }

    if (this.relevantHandoverConfig.type !== expectedType) {
      return;
    }

    if (!this.relevantHandoverConfig.config) {
      this.relevantHandoverConfig.config = {};
    }

    (this.relevantHandoverConfig.config as any)[field] = value;
    this.refreshConfig();
  }

  get forwardToNumber(): string | undefined {
    return this.relevantHandoverConfig?.type === 'handover-to-forward-call'
      ? this.relevantHandoverConfig?.config?.forwardToNumber
      : undefined;
  }

  set forwardToNumber(value: string | undefined) {
    if (value !== undefined) {
      this.updateConfigField('handover-to-forward-call', 'forwardToNumber', value);
    }
  }

  get username() {
    return this.relevantHandoverConfig?.type === 'handover-to-forward-call'
      ? this.relevantHandoverConfig?.config?.username
      : undefined;
  }

  set username(value: string | undefined) {
    if (value !== undefined) {
      this.updateConfigField('handover-to-forward-call', 'username', value);
    }
  }

  get password() {
    return this.relevantHandoverConfig?.type === 'handover-to-forward-call'
      ? this.relevantHandoverConfig?.config?.password
      : undefined;
  }

  set password(value: string | undefined) {
    if (value !== undefined) {
      this.updateConfigField('handover-to-forward-call', 'password', value);
    }
  }

  get closeConversationOnForward(): boolean {
    return this.relevantHandoverConfig?.type === 'handover-to-forward-call'
      ? (this.relevantHandoverConfig?.config?.closeConversationOnForward ?? false)
      : false;
  }

  set closeConversationOnForward(value: boolean) {
    this.updateConfigField('handover-to-forward-call', 'closeConversationOnForward', value);
  }

  get handoverConfigError() {
    if (!this.args.phoneNumberId) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.handover-escalation.error-no-phone-number',
      );
    }

    let result = handoverConfigErrorForConfigType(
      this.clonedHandoverConfig,
      this.configBeingEdited,
    );

    if (result) {
      let label = result.label ? this.intl.t(result.label) : undefined;
      return this.intl.t(result.error, { label });
    }

    return undefined;
  }

  getConfigForComparison(comparison: 'in' | 'nin' | 'default'): HandoverConfiguration | undefined {
    if (!this.numberConfiguration?.handoverConfiguration) {
      return undefined;
    }

    let relevantConfig =
      this.numberConfiguration.handoverConfiguration[comparison] ??
      this.numberConfiguration.handoverConfiguration['default'];

    if (relevantConfig) {
      let { type, config } = relevantConfig;

      return {
        type,
        config: { ...(config ?? {}) },
      } as HandoverConfiguration;
    }

    return undefined;
  }

  @action
  setInitialHandoverConfig() {
    let handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration = {};

    handoverConfig['in'] = this.getConfigForComparison('in');
    handoverConfig['nin'] = this.getConfigForComparison('nin');

    this.clonedHandoverConfig = handoverConfig;
  }

  @action
  setSelectedWorkflow(workflow: $TSFixMe) {
    if (this.relevantHandoverConfig) {
      this.relevantHandoverConfig.type = 'handover-to-workflow';
      this.relevantHandoverConfig.config = {
        workflowId: workflow?.workflowId,
      };
    } else {
      this.clonedHandoverConfig[this.configBeingEdited] = {
        type: 'handover-to-workflow',
        config: {
          workflowId: workflow?.workflowId,
        },
      };
    }
    this.refreshConfig();
  }

  @action
  changeConfigBeingEdited(config: 'in' | 'nin') {
    this.configBeingEdited = config;
  }

  @action
  setHandoverType(type: HandoverConfigurationTools) {
    if (this.relevantHandoverConfig) {
      this.relevantHandoverConfig.type = type;
      this.relevantHandoverConfig.config = {};
    } else {
      this.clonedHandoverConfig[this.configBeingEdited] = {
        type,
        config: {},
      };
    }

    this.refreshConfig();
  }

  @action
  async updateHandover() {
    if (this.handoverConfigError) {
      return;
    }

    this.args.updateHandover(this.clonedHandoverConfig);
  }

  @action
  refreshConfig() {
    this.clonedHandoverConfig = { ...this.clonedHandoverConfig };
  }

  @action
  setAssigneeId(assigneeId: string) {
    if (
      this.relevantHandoverConfig?.type !== 'handover-to-human' &&
      this.relevantHandoverConfig?.type !== 'handover-to-voicemail' &&
      this.relevantHandoverConfig?.type !== 'handover-to-forward-call'
    ) {
      return;
    }

    if (this.relevantHandoverConfig) {
      if (!this.relevantHandoverConfig.config) {
        this.relevantHandoverConfig.config = {};
      }

      this.relevantHandoverConfig.config.assigneeId = assigneeId;

      this.refreshConfig();
    }
  }

  @action
  copyIpAddress(ip: string) {
    navigator.clipboard.writeText(ip);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneHandover::PhoneHandoverForm': typeof PhoneHandoverForm;
  }
}
